import { Component, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';

// Services
import { UtilityService } from '../../../core/services/utility.service';
import { NotificationsService } from '../../../core/services/notifications.service';
import { HiringService } from '../../../pages/hiring/services/hiring.service';

// Models
import {
  I9Employees_Result,
  LookupOnboardLocations_Result,
  LookupOnboardProfiles_Result,
} from '../../../type-definitions/onboard/responses/onboard';
import { I9AuditReportBulk } from '../../../type-definitions/onboard/requests/onboard';
import _ from 'lodash';

@Component({
  selector: 'app-i9-audit-report',
  templateUrl: './i9-audit-report.modal.component.html',
  styleUrls: ['./i9-audit-report.modal.component.css'],
})
export class I9AuditReportModalComponent implements OnInit {
  employeesWithI9: I9Employees_Result[] = [];
  filteredEmployees: I9Employees_Result[] = [];
  reportEmployees: I9Employees_Result[] = [];
  hiringLocations: LookupOnboardLocations_Result[] = [];
  profiles: LookupOnboardProfiles_Result[] = [];
  statuses: OnboardStatus[] = [];
  selectedFilterOnboardIds: Set<string> = new Set<string>();
  selectedReportOnboardIds: Set<string> = new Set<string>();
  allEmployeesPlaceholder = '<All Employees with an I9 Audit Log>';
  dateFormat: string;
  formData: I9AuditSelectionFormData = new I9AuditSelectionFormData();
  mobileMode = false;
  datePickerOptions: {
    dateInputFormat: string;
    containerClass: string;
    showTodayButton: boolean;
    showClearButton: boolean;
    clearPosition: string;
    showWeekNumbers: boolean;
    adaptivePosition: boolean;
  };

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private utilityService: UtilityService,
    private hiringService: HiringService,
    private notificationService: NotificationsService
  ) {}

  ngOnInit(): void {
    this.mobileMode = this.utilityService.isMobileMode();
    this.dateFormat = this.utilityService.getDateFormat();
    this.datePickerOptions = {
      dateInputFormat: this.dateFormat.toUpperCase(),
      containerClass: 'theme-blue',
      showTodayButton: true,
      showClearButton: true,
      clearPosition: 'left',
      showWeekNumbers: false,
      adaptivePosition: true,
    };

    this.getFormData();
  }

  getFormData(): void {
    try {
      this.utilityService.openModal();
      this.hiringService.getOnboardEmployeesWithI9().subscribe(async employees => {
        if (employees == null) {
          this.utilityService.closeModal();
          this.notificationService.showError('Error getting employees with an I9');
        } else {
          if (employees.length != 0) {
            this.employeesWithI9 = employees;
            this.filteredEmployees = _.cloneDeep(employees);

            await this.getFilteredLookupData(employees);
          }

          this.utilityService.closeModal();
        }
      });
    } catch (error) {
      this.utilityService.closeModal();
      throw error;
    }
  }

  async getFilteredLookupData(employees: I9Employees_Result[]): Promise<void> {
    const locationsResult = await this.hiringService.locationGet();
    if (locationsResult != null) {
      // filter results using employee list
      this.hiringLocations = locationsResult
        .filter(location => employees.find(employee => location.OnboardLocationID === employee.OnboardLocationID) != null)
        .sort((location1, location2) => this.sortStringCompare(location1.OnboardLocationName, location2.OnboardLocationName));
    }

    const profilesResult = await this.hiringService.getProfiles('O', null, null, true);
    if (profilesResult != null) {
      // filter results using employee list
      this.profiles = profilesResult
        .filter(profile => employees.find(employee => profile.OnboardProfileID === employee.OnboardProfileID) != null)
        .sort((profile1, profile2) => this.sortStringCompare(profile1.OnboardProfileName, profile2.OnboardProfileName));
    }

    const allStatusstatuses: OnboardStatus[] = [
      { OnboardStatus: 0, Description: 'Pending & Submitted' },
      { OnboardStatus: 1, Description: 'Archived' },
      { OnboardStatus: 2, Description: 'Initialized' },
      { OnboardStatus: 3, Description: 'Initialized, attachments pending' },
      { OnboardStatus: 100, Description: 'Initialized - Employee Removed' }, // psuedo status
    ];

    // filter results using employee list
    this.statuses = allStatusstatuses.filter(status => employees.find(employee => status.OnboardStatus === employee.Status) != null);
  }

  sortStringCompare(value1: string, value2: string): number {
    const casedValue1 = value1.toUpperCase();
    const casedValue2 = value2.toUpperCase();

    if (casedValue1 > casedValue2) return 1;
    if (casedValue1 < casedValue2) return -1;
    return 0;
  }

  employeeNameDisplay(employee: I9Employees_Result): string {
    return `${employee.EmployeeNameDisplay} (${moment(employee.InvitationDate).format(this.dateFormat.toUpperCase())})`;
  }

  applyFilter(): void {
    // get onboard Ids of report records
    const reportOnboardIds = this.reportEmployees.map(employee => employee.OnboardID);

    if (!this.hasFilter()) {
      // if there is no filter, show all employees (minus ones added for report)
      this.filteredEmployees = this.employeesWithI9.filter(employee => !reportOnboardIds.includes(employee.OnboardID));
    } else {
      this.filteredEmployees = this.employeesWithI9.reduce((filtered: I9Employees_Result[], employee: I9Employees_Result) => {
        if (this.hasEmployeeNameFilter()) {
          if (!this.filterEmployeeName(employee.EmployeeNameDisplay)) {
            return filtered;
          }
        }

        if (this.hasInvitationDateFilter()) {
          if (!this.filterInvitationDate(employee.InvitationDate)) {
            return filtered;
          }
        }

        if (this.hasHiredDateFilter()) {
          if (!this.filterHiredDate(employee.ExpectedOrStartDateHired)) {
            return filtered;
          }
        }

        if (this.hasProfileFilter()) {
          if (!this.filterProfile(employee.OnboardProfileID)) {
            return filtered;
          }
        }

        if (this.hasHiringLocationFilter()) {
          if (!this.filterHiringLocation(employee.OnboardLocationID)) {
            return filtered;
          }
        }

        if (this.hasStatusFilter()) {
          if (!this.filterStatus(employee.Status)) {
            return filtered;
          }
        }

        if (this.hasI9CompletedEmployerFilter()) {
          if (!this.filterI9CompletedEmployer(employee.IsI9CompleteMgr)) {
            return filtered;
          }
        }

        // don't add filtered record if it's already in the report list
        if (reportOnboardIds.includes(employee.OnboardID)) {
          return filtered;
        }

        filtered.push(employee);

        return filtered;
      }, []);
    }

    // remove from selected list any employee that is not found
    const filteredOnboardIds = [...this.selectedFilterOnboardIds].filter(
      onboardID => this.filteredEmployees.find(employee => employee.OnboardID == onboardID) != null
    );
    this.selectedFilterOnboardIds = new Set<string>(filteredOnboardIds);
  }

  hasInvitationDateFilter(): boolean {
    return this.formData.invitiationStartDate != null || this.formData.invitiationEndDate != null;
  }

  hasHiredDateFilter(): boolean {
    return this.formData.hiredStartDate != null || this.formData.hiredEndDate != null;
  }

  hasProfileFilter(): boolean {
    return this.formData.profile != null;
  }

  hasHiringLocationFilter(): boolean {
    return this.formData.hiringLocation != null;
  }

  hasStatusFilter(): boolean {
    return this.formData.status != null;
  }

  hasI9CompletedEmployerFilter(): boolean {
    return this.formData.i9CompletedEmployer != null;
  }

  hasEmployeeNameFilter(): boolean {
    return this.formData.employeeFilter != null && this.formData.employeeFilter.length > 0;
  }

  hasFilter(): boolean {
    return (
      this.hasInvitationDateFilter() ||
      this.hasHiredDateFilter() ||
      this.hasProfileFilter() ||
      this.hasHiringLocationFilter() ||
      this.hasStatusFilter() ||
      this.hasI9CompletedEmployerFilter() ||
      this.hasEmployeeNameFilter()
    );
  }

  filterInvitationDate(invitationDate: Date): boolean {
    if (invitationDate == null) {
      return false;
    }

    let selectRecord = false;

    const invitationDateOnly = moment(invitationDate).startOf('day');
    const startDateOnly = this.formData.invitiationStartDate != null ? moment(this.formData.invitiationStartDate).startOf('day') : null;
    const endDateOnly = this.formData.invitiationEndDate != null ? moment(this.formData.invitiationEndDate).startOf('day') : null;

    if (startDateOnly != null && endDateOnly != null) {
      selectRecord = invitationDateOnly.isBetween(startDateOnly, endDateOnly, null, '[]');
    } else if (startDateOnly != null && invitationDateOnly.isSameOrAfter(startDateOnly)) {
      selectRecord = true;
    } else if (endDateOnly != null && invitationDateOnly.isSameOrBefore(endDateOnly)) {
      selectRecord = true;
    }

    return selectRecord;
  }

  filterHiredDate(dateHired?: Date): boolean {
    if (dateHired == null) {
      return false;
    }

    let selectRecord = false;

    const dateHiredDateOnly = moment(dateHired).startOf('day');
    const startDateOnly = this.formData.hiredStartDate != null ? moment(this.formData.hiredStartDate).startOf('day') : null;
    const endDateOnly = this.formData.hiredEndDate != null ? moment(this.formData.hiredEndDate).startOf('day') : null;

    if (startDateOnly != null && endDateOnly != null) {
      selectRecord = dateHiredDateOnly.isBetween(startDateOnly, endDateOnly, null, '[]');
    } else if (startDateOnly != null && dateHiredDateOnly.isSameOrAfter(startDateOnly)) {
      selectRecord = true;
    } else if (endDateOnly != null && dateHiredDateOnly.isSameOrBefore(endDateOnly)) {
      selectRecord = true;
    }

    return selectRecord;
  }

  filterProfile(profileID: number): boolean {
    return this.formData.profile != null && profileID === this.formData.profile;
  }

  filterHiringLocation(locationID?: number): boolean {
    if (locationID == null) return false;
    else return this.formData.hiringLocation != null && locationID === this.formData.hiringLocation;
  }

  filterStatus(status: number): boolean {
    return this.formData.status !== null && status === this.formData.status;
  }

  filterI9CompletedEmployer(isI9CompleteMgr: boolean): boolean {
    if (this.formData.i9CompletedEmployer == null) return false;
    else
      return (
        isI9CompleteMgr == (this.formData.i9CompletedEmployer == 'Y') || !isI9CompleteMgr == (this.formData.i9CompletedEmployer == 'N')
      );
  }

  filterEmployeeName(employeeName: string): boolean {
    if (
      this.formData.employeeFilter == null ||
      this.formData.employeeFilter.length == 0 ||
      employeeName == null ||
      employeeName.length == 0
    ) {
      return false;
    }

    return employeeName.toUpperCase().includes(this.formData.employeeFilter.toUpperCase());
  }

  toggleItemSelection(selectionSet: Set<string>, onboardID: string): void {
    if (this.isItemSelected(selectionSet, onboardID)) {
      selectionSet.delete(onboardID);
    } else {
      selectionSet.add(onboardID);
    }
  }

  isItemSelected(selectionSet: Set<string>, onboardID: string): boolean {
    return selectionSet.has(onboardID);
  }

  selectionFilterEmployees(isSelectAll: boolean): void {
    let filteredOnboardIds: string[] = [];

    if (isSelectAll) {
      filteredOnboardIds = this.filteredEmployees.map(employee => employee.OnboardID);
    }

    this.selectedFilterOnboardIds = new Set<string>(filteredOnboardIds);
  }

  selectionReportEmployees(isSelectAll: boolean): void {
    let filteredOnboardIds: string[] = [];

    if (isSelectAll) {
      filteredOnboardIds = this.reportEmployees.map(employee => employee.OnboardID);
    }

    this.selectedReportOnboardIds = new Set<string>(filteredOnboardIds);
  }

  addToReportList(): void {
    if (this.selectedFilterOnboardIds.size == this.filteredEmployees.length) {
      // add all
      this.reportEmployees = this.reportEmployees.concat(this.filteredEmployees);
      this.filteredEmployees = [];
    } else {
      // add some
      this.reportEmployees = this.reportEmployees.concat(
        this.filteredEmployees.filter(employee => this.isItemSelected(this.selectedFilterOnboardIds, employee.OnboardID))
      );

      this.filteredEmployees = this.filteredEmployees.reduce((filtered: I9Employees_Result[], employee: I9Employees_Result) => {
        if (!this.selectedFilterOnboardIds.has(employee.OnboardID)) {
          filtered.push(employee);
        }

        return filtered;
      }, []);
    }

    this.reportEmployees = this.reportEmployees.sort((emp1, emp2) =>
      this.sortStringCompare(emp1.EmployeeNameDisplay, emp2.EmployeeNameDisplay)
    );

    this.selectedReportOnboardIds = new Set<string>(this.selectedFilterOnboardIds);
    this.selectedFilterOnboardIds.clear();
  }

  removeFromReportList(): void {
    if (this.selectedReportOnboardIds.size == this.reportEmployees.length) {
      // remove all
      this.reportEmployees = [];
    } else {
      // remove some
      this.reportEmployees = this.reportEmployees.reduce((filtered: I9Employees_Result[], employee: I9Employees_Result) => {
        if (!this.selectedReportOnboardIds.has(employee.OnboardID)) {
          filtered.push(employee);
        }

        return filtered;
      }, []);
    }

    this.selectedFilterOnboardIds = new Set<string>(this.selectedReportOnboardIds);
    this.selectedReportOnboardIds.clear();

    // re-apply filter
    this.applyFilter();
  }

  async generateReportClicked(): Promise<void> {
    if (this.reportEmployees.length == 0) {
      const confirmResult = await this.confirmGeneratingForAllUsers();
      if (!confirmResult) return;
    }

    const reportRecords: I9AuditReportBulk[] = (this.reportEmployees.length > 0 ? this.reportEmployees : this.employeesWithI9).map(emp => ({
      OnboardID: emp.OnboardID,
      EmployeeName: emp.EmployeeName,
      InvitationDate: emp.InvitationDate,
    }));

    this.hiringService.processI9AuditReportBulk(reportRecords).subscribe();

    this.activeModal.dismiss();
  }

  async confirmGeneratingForAllUsers(): Promise<boolean> {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 's',
    });
    modalRef.componentInstance.headerText = 'I9 Audit Reports Generation';
    modalRef.componentInstance.bodyText = 'Are you sure you want to generate a report for all Employees with an I9 Audit Log?';

    const result: boolean = await modalRef.result;
    return result;
  }

  cancelClicked(): void {
    this.activeModal.dismiss();
  }
}

export class I9AuditSelectionFormData {
  invitiationStartDate: Date;
  invitiationEndDate: Date;
  hiredStartDate: Date;
  hiredEndDate: Date;
  profile: number;
  hiringLocation: number;
  status: number;
  i9CompletedEmployer: string;
  employeeFilter: string;
}

export class OnboardStatus {
  OnboardStatus: number;
  Description: string;
}
